import { Box, Grid, Icon, useTheme } from '@mui/material';
import React from 'react';
import { CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderData, OrderPageData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { CalendarBoxTypography } from './CalendarBoxContext';
import { orderHasComment } from '../../common/OrderUtils';
import { getContrastColor, LIGHT_CONTRAST_COLOR } from '../../common/ui/getContrastColor';
import { CalendarView } from '../calendarWrapper/CalendarWrapper';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { DEFAULT_ORDER_PAGE_COLOR } from '../../common/theme/mbTokens';

type OrderTopBarProps = {
  orderPage: OrderPageData;
  order: OrderData;
};
// Currently used only for rendering stations mark for speculo but a little bit generic. If we are going
// to render non numbers or more then one item, some ux should be improved
// Now refactored as a top toolbar that arranges relevant icons for the order from left to right
export const OrderTopBar = ({ orderPage, order }: OrderTopBarProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { calendarView } = useCalendar();
  const { t } = useTranslation();
  const theme = useTheme();
  const tileFill = orderPage?.color ?? DEFAULT_ORDER_PAGE_COLOR;

  const customerChoosableResources: number[] = [];
  if (orderPage.shared_resources_allocations?.customer_choosable_resources) {
    orderPage.shared_resources_allocations.customer_choosable_resources.forEach((allocation) => {
      const chosenResource = order.chosen_resources?.find((cr) => cr.resource_id === allocation.resource_id);
      const quantity = chosenResource?.quantity ?? allocation.default;
      if (quantity > 1) {
        customerChoosableResources.push(quantity);
      }
    });
  }

  const resourcesRequired = account?.shared_resources?.automatic_dividable_resources
    ?.map((resource) => {
      const allocation = orderPage.shared_resources_allocations?.automatic_dividable_resources?.find(
        (a) => a.resource_id === resource.id,
      );
      if (!allocation) return null;

      const required = Math.ceil(order.persons / allocation.unit_capacity);
      return required > 1 ? { id: resource.id, required } : null;
    })
    .filter(Boolean);
  return (
    <Grid container direction="row" gap={0.5} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
      <Grid container direction="row" gap={0.5} alignItems="center" sx={{ flex: 1 }}>
        {/* Persons count - first item */}
        <Grid item>
          <CalendarBoxTypography sx={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.2 }}>
            {order.persons}
          </CalendarBoxTypography>
        </Grid>

        {/* Resources required - third items */}
        {resourcesRequired?.map((resource) => (
          <Grid
            key={`resource-${resource?.id}`}
            item
            sx={{
              width: '14px',
              height: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'mb.highlight',
              borderRadius: '4px',
              fontSize: '8px',
              fontWeight: 'bold',
              bgcolor: 'mb.claySoft',
            }}>
            {resource?.required}
          </Grid>
        ))}

        {/* Boolean custom details - show icons for true values in day view */}
        {calendarView === CalendarView.DAY &&
          orderPage.custom_details?.map((custom_detail: any, index: number) => {
            if (custom_detail.type === 'bool' && order.custom_details && order.custom_details[custom_detail.name]) {
              return (
                <Grid key={`bool-custom-${index}`} item>
                  {custom_detail.icon.endsWith('.svg') ? (
                    <img
                      src={custom_detail.icon}
                      alt=""
                      style={{
                        width: '16px',
                        height: '16px',
                        filter: `brightness(0) saturate(100%) invert(${
                          getContrastColor(tileFill) === LIGHT_CONTRAST_COLOR ? '100%' : '0%'
                        })`,
                      }}
                    />
                  ) : (
                    <Icon
                      sx={{
                        width: '16px',
                        height: '16px',
                        fontSize: '16px',
                        fill: getContrastColor(tileFill),
                      }}>
                      {custom_detail.icon}
                    </Icon>
                  )}
                </Grid>
              );
            }
            return null;
          })}

        {customerChoosableResources.length > 0 &&
          customerChoosableResources.map((quantity, index) => {
            if (quantity < 2) {
              return null;
            }
            return (
              <Grid
                key={`choosable-resource-${quantity}-${index}`}
                item
                sx={{
                  width: '14px',
                  height: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid',
                  borderColor: 'mb.control',
                  borderRadius: '4px',
                  fontSize: '8px',
                  fontWeight: 'bold',
                  bgcolor: 'mb.ground',
                }}>
                {quantity}
              </Grid>
            );
          })}
      </Grid>

      {/* Right side icons */}
      <Grid container direction="row" gap={0.5} alignItems="center" sx={{ flex: 0 }}>
        {/* Comment icon */}
        {orderHasComment(order, orderPage) && (
          <Grid item>
            <Box
              component="span"
              role="img"
              aria-label={t('calendar.areas_view.staff_comment')}
              sx={{
                width: 16,
                height: 16,
                borderRadius: '4px',
                bgcolor: 'error.light',
                color: 'error.main',
                fontSize: 11,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              !
            </Box>
          </Grid>
        )}

        {/* Check-in icon */}
        {order.checkin?.created_at && (
          <Grid item>
            <CheckCircle sx={{ width: '12px', height: '12px', fill: theme.palette.primary.main }} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
